import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import OlMap from 'ol/Map';
import GeoJSON from 'ol/format/GeoJSON';
import VectorSource from 'ol/source/Vector';
import type Feature from 'ol/Feature';

import { CanvasToolbarIconComponent } from '../../../icons/canvas-toolbar-icon.component';

export interface ImportedFeaturesPayload {
  features: Feature[];
  mode: 'merge' | 'replace';
}

@Component({
  selector: 'lib-canvas-import-tool',
  standalone: true,
  imports: [CanvasToolbarIconComponent],
  template: `
    <span class="cv-tb-import-wrap">
      @if (importHint(); as hint) {
        <div class="cv-tb-import-hint" role="alert">{{ hint }}</div>
      }

      @if (showMergeDialog()) {
        <div class="cv-import-dialog-backdrop" (click)="cancelImport()">
          <div class="cv-import-dialog" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">

            <!-- Leaf decoration (MahaAgX pattern) -->
            <div class="cv-import-dialog__leaf">
              <img src="/assets-leaf.png" alt="" aria-hidden="true" />
            </div>

            <!-- Icon -->
            <div class="cv-import-dialog__icon">
              <svg viewBox="0 0 24 24" fill="none" style="stroke: var(--canvas-action)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="12" y1="3" x2="12" y2="21"/>
                <polyline points="9 8 7 12 9 16"/>
                <polyline points="15 8 17 12 15 16"/>
              </svg>
            </div>

            <h2 class="cv-import-dialog__title">Import Shapes</h2>
            <p class="cv-import-dialog__body">
              The map already has layers.<br/>What would you like to do?
            </p>

            <div class="cv-import-dialog__actions">
              <button
                type="button"
                class="cv-import-dialog__btn cv-import-dialog__btn--cancel"
                (click)="cancelImport()"
              >Cancel</button>
              <button
                type="button"
                class="cv-import-dialog__btn cv-import-dialog__btn--merge"
                (click)="confirmImport('merge')"
                title="Add imported shapes alongside existing layers"
              >Merge</button>
              <button
                type="button"
                class="cv-import-dialog__btn cv-import-dialog__btn--replace"
                (click)="confirmImport('replace')"
                title="Remove existing layers and load only the imported shapes"
              >Replace</button>
            </div>
          </div>
        </div>
      }

      <input
        #fileInput
        type="file"
        accept=".json,.geojson,application/geo+json,application/json"
        class="cv-tb-import__input"
        (change)="onFileSelected($event)"
      />
      <button
        class="cv-tb__btn"
        (click)="pickFile()"
        title="Import GeoJSON or JSON map data. Use Export → GeoJSON to save drawings for re-import."
        type="button"
      >
        <lib-canvas-toolbar-icon name="import"></lib-canvas-toolbar-icon>
      </button>
    </span>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class ImportToolComponent implements OnDestroy {
  @Input({ required: true }) map!: OlMap;
  @Input({ required: true }) userDrawSource!: VectorSource;

  @Output() featuresImported = new EventEmitter<ImportedFeaturesPayload>();

  @ViewChild('fileInput') private fileInput!: ElementRef<HTMLInputElement>;

  readonly importHint = signal<string | null>(null);
  readonly showMergeDialog = signal(false);

  private pendingFeatures: Feature[] = [];
  private importHintTimer: ReturnType<typeof setTimeout> | null = null;

  pickFile(): void {
    this.fileInput.nativeElement.click();
  }

  ngOnDestroy(): void {
    if (this.importHintTimer) {
      clearTimeout(this.importHintTimer);
      this.importHintTimer = null;
    }
  }

  onFileSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'json' && ext !== 'geojson') {
      this.showImportHint('Only .json or .geojson files can be imported.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result as string;
        const data = JSON.parse(text) as object;
        const format = new GeoJSON();
        const projection = this.map.getView().getProjection();
        const features = format.readFeatures(data, {
          featureProjection: projection ?? undefined,
          dataProjection: 'EPSG:4326',
        });

        if (!features.length) {
          this.showImportHint('No features found in that file.');
          return;
        }

        for (const f of features) {
          f.set('cvImported', true);
        }

        if (this.userDrawSource.getFeatures().length > 0) {
          this.pendingFeatures = features;
          this.showMergeDialog.set(true);
        } else {
          this.applyImport(features, 'merge');
        }
      } catch (e) {
        console.error('Import failed', e);
        this.showImportHint('Could not read that file as GeoJSON.');
      }
    };
    reader.onerror = () => {
      console.error('Unable to read file');
      this.showImportHint('Could not read the file.');
    };
    reader.readAsText(file);
  }

  confirmImport(mode: 'merge' | 'replace'): void {
    const features = this.pendingFeatures;
    this.pendingFeatures = [];
    this.showMergeDialog.set(false);
    this.applyImport(features, mode);
  }

  cancelImport(): void {
    this.pendingFeatures = [];
    this.showMergeDialog.set(false);
  }

  private applyImport(features: Feature[], mode: 'merge' | 'replace'): void {
    if (mode === 'replace') {
      this.userDrawSource.clear();
    }
    this.userDrawSource.addFeatures(features);
    this.featuresImported.emit({ features, mode });

    const extent = this.userDrawSource.getExtent();
    if (extent && extent.length >= 4 && extent.every((n) => Number.isFinite(n))) {
      this.map.getView().fit(extent as [number, number, number, number], {
        padding: [48, 48, 48, 48],
        maxZoom: 16,
        duration: 350,
      });
    }
  }

  private showImportHint(text: string): void {
    if (this.importHintTimer) {
      clearTimeout(this.importHintTimer);
      this.importHintTimer = null;
    }
    this.importHint.set(text);
    this.importHintTimer = setTimeout(() => {
      this.importHint.set(null);
      this.importHintTimer = null;
    }, 5500);
  }
}
